import { neverCancel } from '@mimir/common/async/async';
import { makePriority, PrioritySemaphore } from '@mimir/common/async/prioritySemaphore';
import { invariant } from '@mimir/common/util/invariant';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { MediaSegmentsResponse } from '@mimir/common/dto/mediaDtos';
import type { VideoApiActions } from './apiActions';

function tryGetUrl(urls: MediaSegmentsResponse, streamId: string, index: number): string | undefined {
  const streamUrls = urls[streamId];
  if (streamUrls == null) {
    return undefined;
  }
  return streamUrls[index];
}

export interface SegmentUrlResolver {
  getMediaSegmentUrl(itemId: string, streamId: StreamRoleId, segmentIndex: number): Promise<string>;
}

/**
 * Remembers results of previous media segment URL requests, and avoids doing
 * additional request when not needed. For this to be efficient, it is assumed
 * that the cache will only be used for one itemId at a time, and that the
 * segment URLs are mostly requested in index order.
 */
export class SegmentUrlCache implements SegmentUrlResolver {
  private semaphore = new PrioritySemaphore(1);
  private streamHints: Set<StreamRoleId>;
  private urls: MediaSegmentsResponse | null = null;
  private itemId: string | null = null;
  constructor(
    private api: VideoApiActions,
    private signedForMethod: 'get' | 'put',
    streamHints: StreamRoleId[]
  ) {
    this.streamHints = new Set<StreamRoleId>(streamHints);
  }

  getMediaSegmentUrl(
    itemId: string,
    streamId: StreamRoleId,
    index: number
  ): Promise<string> {
    /*
      Make sure we don't needlessly request the same information multiple times
      due to concurrency.
    */
    return this.semaphore.limit(() => this.getMediaSegmentUrlCore(itemId, streamId, index), makePriority(), neverCancel);
  }

  async getMediaSegmentUrlCore(
    itemId: string,
    streamId: StreamRoleId,
    index: number
  ): Promise<string> {
    if (this.itemId == itemId && this.urls != null) {
      const cachedUrl = tryGetUrl(this.urls, streamId, index);
      if (cachedUrl != null) {
        return cachedUrl;
      }
    }

    // TODO implement actual cache rather than calling the API every time.
    this.streamHints.add(streamId);
    this.urls = await this.api.getMediaSegmentUrls(
      itemId,
      Array.from(this.streamHints),
      this.signedForMethod,
      index
    );
    this.itemId = itemId;

    const url = tryGetUrl(this.urls, streamId, index);
    invariant(
      url != null,
      'Missing url for segment index ' + index + ' of stream ' + streamId
    );
    return url;
  }
}
